<!-- Flagship Projects Section -->
<section class="py-16 lg:py-24 bg-white relative border-t border-[#0e3b2e]/10">
  <div class="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 space-y-10">

    <!-- Header: eyebrow + two-tone title + intro + actions -->
    <div class="flex flex-col lg:flex-row lg:items-start justify-between gap-8">
      <div class="space-y-4">
        <span class="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#e6b012]">
          <span class="h-4 w-1 rounded-full bg-[#e6b012]"></span> Featured Projects
        </span>
        <h2 class="max-w-2xl text-2xl sm:text-3xl lg:text-4xl font-black text-[#0e3b2e] tracking-tight leading-[1.1]">
          Tracking National <span class="text-[#bcb365]">Development Projects</span> Across All Sectors
        </h2>
      </div>

      <div class="lg:max-w-sm lg:text-right space-y-4">
        <p class="text-sm text-slate-600 leading-relaxed">
          Explore government-led projects tracked through the MEL system — from planning to execution, with live
          progress and outcome data aligned to Vision 2050.
        </p>
        <div class="flex flex-wrap items-center gap-3 lg:justify-end">
          <a
            routerLink="/projects"
            class="px-6 py-2.5 rounded-xl bg-[#e6b012] hover:bg-[#d4a00e] text-[#ffffff] text-xs font-m tracking-wide transition-all shadow-sm flex items-center gap-1.5"
          >
            <span>View All Projects</span>
            <span class="text-sm leading-none">›</span>
          </a>
          <a
            class="px-6 py-2.5 rounded-xl border border-[#e6b012] text-[#e6b012] hover:bg-[#e6b012]/10 text-xs font-m tracking-wide transition-all flex items-center gap-1.5 cursor-pointer"
          >
            <mat-icon class="!text-[15px] !w-[15px] !h-[15px]">download</mat-icon>
            <span>Download Report</span>
          </a>
        </div>
      </div>
    </div>

    <!-- Sector filter tabs -->
    <div class="flex flex-wrap items-center gap-2.5">
      @for (s of sectors; track s) {
        <button
          type="button"
          (click)="activeSector.set(s)"
          class="rounded-full px-4 py-2 text-xs font-bold tracking-wide transition-all border"
          [class]="
            activeSector() === s
              ? 'bg-[#0e3b2e] border-[#0e3b2e] text-white shadow-sm'
              : 'bg-white border-slate-200 text-slate-500 hover:border-[#0e3b2e]/30 hover:text-[#0e3b2e]'
          "
        >
          {{ s }}
        </button>
      }
    </div>

    <!-- Project Cards Grid (4 Columns) -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      @for (p of filtered(); track p.title) {
        <div
          class="rounded-2xl bg-white border border-slate-200/80 overflow-hidden shadow-xs hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group flex flex-col"
        >
          <!-- Image -->
          <div class="relative h-44 overflow-hidden">
            <img
              [src]="p.img"
              [alt]="p.title"
              class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </div>

          <!-- Body -->
          <div class="flex flex-1 flex-col p-5">
            <span class="mb-3 inline-flex w-fit rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider text-amber-700">
              {{ p.sector }}
            </span>
            <h3 class="text-[15px] font-bold text-[#0e3b2e] leading-snug">{{ p.title }}</h3>
            <p class="mt-2 text-xs text-slate-500 leading-relaxed line-clamp-2">{{ p.desc }}</p>

            <!-- Progress -->
            <div class="mt-auto pt-4 space-y-1.5">
              <div class="flex items-center justify-between text-[11px] font-bold">
                <span class="text-slate-500">Progress</span>
                <span class="text-[#e6b012]">{{ p.progress }}%</span>
              </div>
              <div class="w-full h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  class="h-full rounded-full bg-gradient-to-r from-[#0e3b2e] via-[#0f5140] to-emerald-400"
                  [style.width.%]="p.progress"
                ></div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="flex items-center justify-between border-t border-slate-100 px-5 py-3">
            <span class="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              {{ p.ministry }} · {{ p.period }}
            </span>
            <a routerLink="/projects" class="text-[10px] font-extrabold uppercase tracking-wide text-slate-500 transition-colors hover:text-[#e6b012]">
              Details ›
            </a>
          </div>
        </div>
      } @empty {
        <div class="col-span-full flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center">
          <mat-icon class="!text-3xl text-slate-300 mb-2">inbox</mat-icon>
          <p class="text-sm text-slate-500">No tracked projects in {{ activeSector() }} yet.</p>
        </div>
      }
    </div>

    <!-- Investment Stats Banner -->
    <div
      class="p-8 lg:p-10 rounded-3xl text-white grid grid-cols-2 md:grid-cols-4 gap-6 shadow-xl relative overflow-hidden"
      style="background: linear-gradient(110deg, #0b2e24 0%, #0f5140 55%, #14926f 100%);"
    >
      <div class="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-[#e6b012]/10"></div>
      <div class="relative z-10">
        <p class="text-3xl lg:text-4xl font-black text-[#e6b012]">120+</p>
        <p class="mt-1 text-[11px] font-semibold text-white/75 uppercase tracking-wider">Active Projects Tracked</p>
      </div>
      <div class="relative z-10">
        <p class="text-3xl lg:text-4xl font-black text-white">18</p>
        <p class="mt-1 text-[11px] font-semibold text-white/75 uppercase tracking-wider">Government Ministries</p>
      </div>
      <div class="relative z-10">
        <p class="text-3xl lg:text-4xl font-black text-white">TSh <span class="text-[#e6b012]">4.2T</span></p>
        <p class="mt-1 text-[11px] font-semibold text-white/75 uppercase tracking-wider">Total Budget under MEL</p>
      </div>
      <div class="relative z-10">
        <p class="text-3xl lg:text-4xl font-black text-white">92%</p>
        <p class="mt-1 text-[11px] font-semibold text-white/75 uppercase tracking-wider">Reporting Compliance Rate</p>
      </div>
    </div>

  </div>
</section>
